if (!customElements.get('favourites-page-alard')) {
  class FavouritesPageAlard extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.grid = this.querySelector('[data-favourites-grid]');
      this.emptyState = this.querySelector('[data-favourites-empty]');
      this.status = this.querySelector('[data-favourites-status]');
      this.onFavouritesChange = this.handleFavouritesChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      window.addEventListener(window.AlardFavourites.changeEventName, this.onFavouritesChange);
      this.addEventListener('submit', this.onSubmit);
      this.render(window.AlardFavourites.read());
    }

    disconnectedCallback() {
      window.removeEventListener(window.AlardFavourites.changeEventName, this.onFavouritesChange);
      this.removeEventListener('submit', this.onSubmit);
      this.isInitialized = false;
    }

    handleFavouritesChange(event) {
      const items = event.detail?.items || window.AlardFavourites.read();
      const nextSignature = this.getItemsSignature(items);
      if (nextSignature !== this.itemsSignature) this.render(items);
    }

    getItemsSignature(items) {
      return items.map((item) => String(item.productId)).sort().join('|');
    }

    async render(items) {
      this.itemsSignature = this.getItemsSignature(items);
      const renderId = `${Date.now()}-${Math.random()}`;
      this.renderId = renderId;

      if (!items.length) {
        this.grid.innerHTML = '';
        this.grid.hidden = true;
        this.emptyState.hidden = false;
        this.status.hidden = true;
        return;
      }

      this.emptyState.hidden = true;
      this.grid.hidden = true;
      this.status.hidden = false;
      this.status.classList.remove('visually-hidden');
      this.status.textContent = 'Loading your favourites…';

      const favouriteProducts = await Promise.all(
        [...items].reverse().map(async (item) => {
          try {
            return { item, product: await this.fetchProduct(item.handle) };
          } catch (error) {
            return { item, product: null };
          }
        })
      );

      if (this.renderId !== renderId) return;

      this.grid.innerHTML = favouriteProducts
        .map(({ item, product }) => (product ? this.renderProduct(product, item) : this.renderUnavailableProduct(item)))
        .join('');
      this.grid.hidden = false;
      this.status.textContent = `${items.length} saved ${items.length === 1 ? 'product' : 'products'}`;
      this.status.classList.add('visually-hidden');
    }

    async fetchProduct(handle) {
      if (!handle) throw new Error('Missing product handle');
      const rootUrl = this.dataset.rootUrl?.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl || '/'}/`;
      const response = await fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      return response.json();
    }

    renderProduct(product, item) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const selectedVariant =
        variants.find((variant) => String(variant.id) === String(item.variantId)) ||
        variants.find((variant) => variant.available) ||
        variants[0];

      if (!selectedVariant) return this.renderUnavailableProduct(item);

      const rootUrl = this.dataset.rootUrl?.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl || '/'}/`;
      const productUrl = `${rootUrl}products/${encodeURIComponent(product.handle)}`;
      const selectedUrl = `${productUrl}?variant=${selectedVariant.id}`;
      const selectedImage = this.getVariantImage(selectedVariant) || product.featured_image || item.image || '';
      const optimizedImage = this.optimizeImage(selectedImage);
      const title = product.title || item.title || 'Product';
      const price = this.formatMoney(selectedVariant.price);
      const comparePrice =
        Number(selectedVariant.compare_at_price) > Number(selectedVariant.price)
          ? this.formatMoney(selectedVariant.compare_at_price)
          : '';
      const isAvailable = Boolean(selectedVariant.available);
      const addLabel = this.dataset.addLabel || window.variantStrings?.addToCart || 'Add to cart';
      const soldOutLabel = this.dataset.soldOutLabel || window.variantStrings?.soldOut || 'Sold out';
      const uniqueId = `${product.id}-${selectedVariant.id}`;
      const imageMarkup = optimizedImage
        ? `<a class="best-seller-card-alard__media-link" href="${this.escapeHtml(selectedUrl)}" data-card-product-link aria-label="View ${this.escapeHtml(title)}"><img class="best-seller-card-alard__image" src="${this.escapeHtml(optimizedImage)}" width="800" height="800" loading="lazy" alt="${this.escapeHtml(title)}"></a>`
        : '<div class="best-seller-card-alard__image main-favourites-alard__image-placeholder" aria-hidden="true"></div>';

      return `
        <div class="card-wrapper product-card-wrapper product-card-wrapper--alard">
          <best-seller-card-alard
            class="best-seller-card-alard product-card-alard"
            data-product-url="${this.escapeHtml(productUrl)}"
            data-product-id="${this.escapeHtml(String(product.id))}"
            data-product-handle="${this.escapeHtml(product.handle)}"
            data-product-title="${this.escapeHtml(title)}"
            data-selected-variant-id="${this.escapeHtml(String(selectedVariant.id))}"
            data-current-price="${this.escapeHtml(price)}"
            data-current-image="${this.escapeHtml(optimizedImage)}"
          >
            <div class="best-seller-card-alard__media">
              ${imageMarkup}
              <div class="best-seller-card-alard__media-header">
                <div class="best-seller-card-alard__badges">${this.renderBadges(
                  product,
                  selectedVariant
                )}</div>
                ${this.renderFavouriteButton(title)}
              </div>
            </div>
            <div class="best-seller-card-alard__details">
              <div class="best-seller-card-alard__information">
                <h2 class="best-seller-card-alard__title"><a href="${this.escapeHtml(
                  selectedUrl
                )}" data-card-product-link>${this.escapeHtml(title)}</a></h2>
                ${this.renderVariants(product, variants, selectedVariant)}
                <div class="best-seller-card-alard__prices">
                  <span class="best-seller-card-alard__price" data-card-price>${this.escapeHtml(price)}</span>
                  <s class="best-seller-card-alard__compare-price${comparePrice ? '' : ' is-hidden'}" data-card-compare-price>${this.escapeHtml(
                    comparePrice
                  )}</s>
                </div>
              </div>
              <div class="best-seller-card-alard__form">
                <div class="product-form__error-message-wrapper" role="alert" hidden><span class="product-form__error-message"></span></div>
                <form method="post" action="${this.escapeHtml(window.routes?.cart_add_url || '/cart/add')}" id="FavouriteProductForm-${this.escapeHtml(
                  uniqueId
                )}" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-favourites-product-form>
                  <input class="product-variant-id" type="hidden" name="id" value="${this.escapeHtml(String(selectedVariant.id))}">
                  <button type="submit" name="add" class="best-seller-card-alard__add" ${
                    isAvailable ? '' : 'disabled aria-disabled="true"'
                  }>
                    <span>${this.escapeHtml(isAvailable ? addLabel : soldOutLabel)}</span>
                    <div class="loading__spinner hidden"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg></div>
                  </button>
                </form>
              </div>
            </div>
          </best-seller-card-alard>
        </div>`;
    }

    renderUnavailableProduct(item) {
      const title = item.title || 'Unavailable product';
      const productUrl = this.safeProductUrl(item.url);
      const image = this.safeImageUrl(item.image);
      const imageMarkup = image
        ? `<a class="best-seller-card-alard__media-link" href="${this.escapeHtml(productUrl)}"><img class="best-seller-card-alard__image" src="${this.escapeHtml(
            image
          )}" width="800" height="800" loading="lazy" alt="${this.escapeHtml(title)}"></a>`
        : '<div class="best-seller-card-alard__image main-favourites-alard__image-placeholder" aria-hidden="true"></div>';

      return `
        <div class="card-wrapper product-card-wrapper product-card-wrapper--alard">
          <best-seller-card-alard class="best-seller-card-alard product-card-alard" data-product-url="${this.escapeHtml(
            productUrl
          )}" data-product-id="${this.escapeHtml(String(item.productId))}" data-product-handle="${this.escapeHtml(
            item.handle || ''
          )}" data-product-title="${this.escapeHtml(title)}" data-selected-variant-id="${this.escapeHtml(
            String(item.variantId || '')
          )}" data-current-price="${this.escapeHtml(item.price || '')}" data-current-image="${this.escapeHtml(image)}">
            <div class="best-seller-card-alard__media">
              ${imageMarkup}
              <div class="best-seller-card-alard__media-header"><div></div>${this.renderFavouriteButton(title)}</div>
            </div>
            <div class="best-seller-card-alard__details">
              <div class="best-seller-card-alard__information">
                <h2 class="best-seller-card-alard__title"><a href="${this.escapeHtml(productUrl)}" data-card-product-link>${this.escapeHtml(
                  title
                )}</a></h2>
                ${item.price ? `<div class="best-seller-card-alard__prices"><span class="best-seller-card-alard__price">${this.escapeHtml(item.price)}</span></div>` : ''}
                <p class="main-favourites-alard__unavailable-note">This product is currently unavailable.</p>
              </div>
              <button class="best-seller-card-alard__add" type="button" disabled aria-disabled="true"><span>Unavailable</span></button>
            </div>
          </best-seller-card-alard>
        </div>`;
    }

    async handleSubmit(event) {
      const form = event.target.closest('[data-favourites-product-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();

      const submitButton = form.querySelector('[type="submit"]');
      if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

      const errorWrapper = form.parentElement.querySelector('.product-form__error-message-wrapper');
      const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
      const spinner = submitButton.querySelector('.loading__spinner');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      spinner?.classList.remove('hidden');
      if (errorWrapper) errorWrapper.hidden = true;

      try {
        const formData = new FormData(form);
        if (cart) {
          formData.append(
            'sections',
            cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(',')
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }

        const config = window.fetchConfig ? window.fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/json' } };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(window.routes?.cart_add_url || form.action, config);
        const result = await response.json();

        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this product.');

        if (cart) {
          cart.renderContents(result);
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        if (errorWrapper) errorWrapper.hidden = false;
        if (errorMessage) errorMessage.textContent = error.message || 'Unable to add this product.';
      } finally {
        submitButton.classList.remove('loading');
        spinner?.classList.add('hidden');
        const selectedVariantId = form.querySelector('[name="id"]')?.value;
        const selectedVariantButton = form.closest('best-seller-card-alard')?.querySelector(`[data-variant-id="${selectedVariantId}"]`);
        if (!selectedVariantButton || selectedVariantButton.dataset.available === 'true') {
          submitButton.removeAttribute('aria-disabled');
        }
      }
    }

    renderFavouriteButton(title) {
      return `<button class="best-seller-card-alard__favorite is-favourite" type="button" data-favourite-toggle aria-pressed="true" aria-label="Remove ${this.escapeHtml(
        title
      )} from favourites" title="Remove from favourites"><svg width="19" height="17" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.16 2.61C7.49.67 4.72.14 2.63 1.93.54 3.71.25 6.69 1.89 8.8c1.36 1.75 5.49 5.45 6.84 6.65.15.13.23.2.31.23.08.02.16.02.24 0 .09-.03.16-.1.32-.23 1.35-1.2 5.47-4.9 6.84-6.65 1.64-2.11 1.38-5.11-.74-6.87-2.13-1.77-4.87-1.26-6.54.68Z" stroke="currentColor" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>`;
    }

    renderVariants(product, variants, selectedVariant) {
      if (variants.length <= 1) return '';

      const variantButtons = variants
        .map((variant) => {
          const label = variant.public_title || variant.title || variant.options?.join(' / ') || 'Variant';
          const image = this.optimizeImage(this.getVariantImage(variant) || product.featured_image || '');
          const comparePrice =
            Number(variant.compare_at_price) > Number(variant.price) ? this.formatMoney(variant.compare_at_price) : '';
          const isSelected = String(variant.id) === String(selectedVariant.id);

          return `<button type="button" class="best-seller-card-alard__variant${
            isSelected ? ' is-active' : ''
          }" data-variant-id="${this.escapeHtml(String(variant.id))}" data-price="${this.escapeHtml(
            this.formatMoney(variant.price)
          )}" data-compare-price="${this.escapeHtml(comparePrice)}" data-available="${Boolean(
            variant.available
          )}" data-image-src="${this.escapeHtml(image)}" aria-pressed="${isSelected}">${this.escapeHtml(label)}</button>`;
        })
        .join('');

      return `<div class="best-seller-card-alard__variants" aria-label="Choose a variant">${variantButtons}</div>`;
    }

    renderBadges(product, selectedVariant) {
      const badges = [];
      const showAllTags = this.dataset.showAllTags === 'true';
      const limit = showAllTags ? Number.POSITIVE_INFINITY : Math.max(1, Number(this.dataset.badgeLimit) || 3);
      const prefix = this.dataset.badgePrefix || 'alard__badge:';
      const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
      const plainBadgeTags = String(this.dataset.plainBadgeTags || '')
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean);

      if (this.dataset.enableBadges === 'true') {
        tags.forEach((tag) => {
          const normalizedTag = String(tag).trim();
          if (badges.length >= limit) return;
          const label = normalizedTag.startsWith(prefix)
            ? normalizedTag.slice(prefix.length).trim()
            : showAllTags || plainBadgeTags.includes(normalizedTag.toLowerCase())
              ? normalizedTag
              : '';
          if (!label) return;
          const loweredLabel = label.toLowerCase();
          const isSoldOut = ['sold out', 'sold-out', 'soldout'].some((value) => loweredLabel.includes(value));
          const style = isSoldOut
            ? 'sold-out'
            : loweredLabel.includes('sale')
              ? 'sale'
              : loweredLabel.includes('fresh harvest') ||
                  loweredLabel.includes('new arrival') ||
                  loweredLabel.includes('new')
              ? 'new'
              : loweredLabel.includes('best seller') || loweredLabel.includes('bestseller')
                ? 'best-seller'
                : loweredLabel.includes('limited edition') || loweredLabel.includes('limited')
                  ? 'limited'
              : loweredLabel.includes('exclusive')
                ? 'exclusive'
                : 'light';
          badges.push({ label, style });
        });
      }

      if (!product.available && badges.length < limit) {
        badges.push({ label: this.dataset.soldOutLabel || 'Sold out', style: 'sold-out' });
      } else if (
        this.dataset.showSaleBadge === 'true' &&
        Number(selectedVariant.compare_at_price) > Number(selectedVariant.price) &&
        badges.length < limit
      ) {
        badges.push({ label: 'Sale', style: 'sale' });
      }

      return badges
        .map(
          (badge) =>
            `<span class="alard__badge best-seller-card-alard__badge best-seller-card-alard__badge--${badge.style}">${this.escapeHtml(
              badge.label
            )}</span>`
        )
        .join('');
    }

    getVariantImage(variant) {
      if (typeof variant.featured_image === 'string') return variant.featured_image;
      return variant.featured_image?.src || '';
    }

    optimizeImage(image) {
      const safeImage = this.safeImageUrl(image);
      if (!safeImage) return '';
      const url = new URL(safeImage);
      url.searchParams.set('width', '800');
      return url.href;
    }

    safeProductUrl(value) {
      try {
        const url = new URL(value || '/', window.location.origin);
        return url.origin === window.location.origin ? url.href : '/';
      } catch (error) {
        return '/';
      }
    }

    safeImageUrl(value) {
      try {
        if (!value) return '';
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch (error) {
        return '';
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  customElements.define('favourites-page-alard', FavouritesPageAlard);
}
